// ─── Roles ───────────────────────────────────────────────
export const ROLES = ['Admin', 'Operator', 'Viewer'];

export const ROLE_COLOR = {
  Admin:    '#00e5a0',
  Operator: '#5b9cf6',
  Viewer:   '#b89de8',
};

// Default permissions per role
export const DEFAULT_ROLE_PERMS = {
  Admin: {
    // Assets
    canViewAssets: true, canRegisterAssets: true, canEditAssets: true, canDeleteAssets: true,
    // Operations
    canCheckout: true, canApprove: true, canViewTracking: true,
    // Facilities
    canViewFacilities: true, canManageFacilities: true,
    // Reports
    canViewReports: true, canExport: true,
    // System
    canManageUsers: true, canConfig: true,
    // Legacy aliases (kept for backward compat)
    canDelete: true,
  },
  Operator: {
    // Assets
    canViewAssets: true, canRegisterAssets: true, canEditAssets: true, canDeleteAssets: false,
    // Operations
    canCheckout: true, canApprove: false, canViewTracking: true,
    // Facilities
    canViewFacilities: true, canManageFacilities: false,
    // Reports
    canViewReports: true, canExport: true,
    // System
    canManageUsers: false, canConfig: false,
    // Legacy
    canDelete: false,
  },
  Viewer: {
    // Assets
    canViewAssets: true, canRegisterAssets: false, canEditAssets: false, canDeleteAssets: false,
    // Operations
    canCheckout: false, canApprove: false, canViewTracking: true,
    // Facilities
    canViewFacilities: true, canManageFacilities: false,
    // Reports
    canViewReports: true, canExport: false,
    // System
    canManageUsers: false, canConfig: false,
    // Legacy
    canDelete: false,
  },
};

// ─── Asset status ─────────────────────────────────────────
export const STATUS_COLOR = {
  Available:           '#00e5a0',
  'In Use':            '#5b9cf6',
  'Under Maintenance': '#f5a623',
  'In Transit':        '#b89de8',
  Pending:             '#f5a623',
  Approved:            '#00e5a0',
  Rejected:            '#e05757',
  Completed:           '#5b9cf6',
  Active:              '#00e5a0',
  Inactive:            '#e05757',
};

export const PRIORITY_COLOR = {
  Low:    '#a0b4c0',
  Normal: '#5b9cf6',
  High:   '#f5a623',
  Urgent: '#e05757',
};

// ─── Delivery status ──────────────────────────────────────
export const DEL_STATUS_COLOR = {
  Pending:      '#f5a623',
  Dispatched:   '#b89de8',
  'In Transit': '#5b9cf6',
  Delivered:    '#00e5a0',
  Failed:       '#e05757',
};

export const DEL_STATUS_ICON = {
  Pending:      '⏳',
  Dispatched:   '📦',
  'In Transit': '🚚',
  Delivered:    '✅',
  Failed:       '✕',
};

export const DELIVERY_PIPELINE = ['Pending', 'Dispatched', 'In Transit', 'Delivered'];

// ─── Facility types ───────────────────────────────────────
export const FACILITY_TYPES = [
  'Headquarters', 'Depot', 'Warehouse', 'Store',
  'Factory', 'Office', 'Data Center',
];

// ─── Floor map zone colours ───────────────────────────────
export const ZONE_COLORS  = ['#5b9cf640','#00e5a040','#f5a62340','#b89de840','#e0575740','#22d3ee40','#a3e63540','#f472b640'];
export const ZONE_BORDER  = ['#5b9cf6',  '#00e5a0',  '#f5a623',  '#b89de8',  '#e05757',  '#22d3ee',  '#a3e635',  '#f472b6'];
export const ZONE_LABELS_PRESET = [
  'Zone A', 'Zone B', 'Zone C', 'Loading Bay', 'Server Room',
  'Storage Room', 'Office Area', 'Receiving Dock', 'Exit Gate',
  'Main Entrance', 'Warehouse Floor', 'Maintenance Bay',
];

// ─── Default system settings ──────────────────────────────
export const DEFAULT_SETTINGS = {
  systemName:    'RFID WMS',
  companyName:   'MyCompany Sdn. Bhd.',
  timezone:      'Asia/Kuala_Lumpur',
  currency:      'MYR',
  language:      'en',
  itemsPerPage:  20,
  lowStockAlert: 10,

  apiEndpoint:    '',
  apiKey:         '',
  webhookUrl:     '',
  webhookEnabled: false,
  syncInterval:   30,

  emailAlerts:      false,
  alertEmail:       '',
  notifyOnTransfer: true,
  notifyOnCheckout: true,
  notifyOnOffline:  true,
  notifyOnLowStock: false,

  epcCompanyPrefix: '303',
  epcFormat:        'GS1-EPC-Gen2',
  invPrefix:        'INV',
  autoGenEPC:       true,
  autoGenInv:       true,

  sessionTimeout:    480,
  requireMFA:        false,
  passwordMinLength: 8,
  maxLoginAttempts:  5,

  carriers: [
    { id: 'CAR001', name: 'Pos Laju',    type: 'Domestic',      contact: '1300-30-0300', website: 'poslaju.com.my',       active: true  },
    { id: 'CAR002', name: 'DHL Express', type: 'International', contact: '1800-88-8838', website: 'dhl.com',              active: true  },
    { id: 'CAR003', name: 'J&T Express', type: 'Domestic',      contact: '1800-80-8888', website: 'jtexpress.my',          active: true  },
    { id: 'CAR004', name: 'GDex',        type: 'Domestic',      contact: '1300-22-4339', website: 'gdexpress.com',         active: true  },
    { id: 'CAR005', name: 'City-Link',   type: 'Domestic',      contact: '1300-88-0001', website: 'citylinkexpress.com',   active: true  },
    { id: 'CAR006', name: 'Skynet',      type: 'Domestic',      contact: '1800-18-8608', website: 'skynet.com.my',         active: false },
    { id: 'CAR007', name: 'Aramex',      type: 'International', contact: '1800-88-1811', website: 'aramex.com',            active: true  },
  ],
  defaultCarrier:      'Pos Laju',
  requireTrackingNo:   true,
  autoCreateDelivery:  false,

  assetTypes: [
    { id: 'AT001', name: 'Laptop',            icon: '💻', color: '#5b9cf6' },
    { id: 'AT002', name: 'Server',            icon: '🖥',  color: '#b89de8' },
    { id: 'AT003', name: 'Forklift',          icon: '🚜', color: '#f5a623' },
    { id: 'AT004', name: 'Scanner',           icon: '📷', color: '#00e5a0' },
    { id: 'AT005', name: 'Tablet',            icon: '📱', color: '#5b9cf6' },
    { id: 'AT006', name: 'Projector',         icon: '📽',  color: '#b89de8' },
    { id: 'AT007', name: 'Printer',           icon: '🖨',  color: '#a0b4c0' },
    { id: 'AT008', name: 'Network Switch',    icon: '🔌', color: '#22d3ee' },
    { id: 'AT009', name: 'UPS',               icon: '🔋', color: '#f5a623' },
    { id: 'AT010', name: 'CCTV Camera',       icon: '📹', color: '#e05757' },
    { id: 'AT011', name: 'Fire Extinguisher', icon: '🧯', color: '#e05757' },
    { id: 'AT012', name: 'Power Tool',        icon: '🔧', color: '#a0b4c0' },
    { id: 'AT013', name: 'Measuring Device',  icon: '📏', color: '#00e5a0' },
  ],

  rfidReaders: [],
  defaultProtocol:    'LLRP',
  defaultReadInterval: 500,

  assetConditions: [
    { id: 'excellent',    name: 'Excellent',    color: '#00e5a0' },
    { id: 'good',         name: 'Good',         color: '#5b9cf6' },
    { id: 'fair',         name: 'Fair',         color: '#f5a623' },
    { id: 'needs-repair', name: 'Needs Repair', color: '#e05757' },
  ],

  decommissionHide:          'never',
  decommissionRequireReason: false,
  decommissionNotify:        false,
  epcRecycleEnabled:         false,
  epcRecycleAutoRelease:     false,
};
